import {
  isModelKey,
  MODEL_CATALOGUE,
  MODEL_PART,
  MODEL_REASONS,
  type ModelPartData,
  type ModelReason,
} from "@esi/shared";
import { Cpu, Info, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatList } from "../i18n/format.ts";

/**
 * The models that wrote an answer, from its stored `data-model` part (spec `trust-indicators`
 * "Model line on answers", model-picking D11): display names from the catalogue, never a
 * provider or profile ID, and why another version answered. An older answer without `reason`
 * reads its `fallback`. Null when the message has no such part (older answers, hidden
 * messages, a turn with no step).
 */
export function answerModels(parts: readonly { type: string }[]): {
  names: string[];
  reason: ModelReason;
} | null {
  const part = parts.find((p) => p.type === MODEL_PART) as { data?: ModelPartData } | undefined;
  const names = (part?.data?.models ?? []).flatMap((key) =>
    isModelKey(key) ? [MODEL_CATALOGUE[key].displayName] : [],
  );
  const stored = part?.data?.reason;
  const fallback = part?.data?.fallback;
  const reason: ModelReason =
    stored !== undefined && (MODEL_REASONS as readonly string[]).includes(stored)
      ? stored
      : fallback === "policy"
        ? "policy"
        : fallback === "unavailable"
          ? "unavailable"
          : "first";
  return names.length > 0 ? { names, reason } : null;
}

/**
 * "Claude Sonnet 5.5", and, when it was not the first choice, one plain sentence saying why
 * ("The newest version was unavailable", "The version set by your organisation was
 * unavailable"; never which setting refused a choice): a quiet 12px muted mark for the
 * answer's trust line, never a badge.
 */
export function ModelMark({ names, reason }: { names: string[]; reason: ModelReason }) {
  const { t } = useTranslation();
  const ReasonIcon = reason === "policy" || reason === "provider_withdrawn" ? Info : RefreshCw;
  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
      <span className="inline-flex min-w-0 items-center gap-1">
        <Cpu aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
        <span className="sr-only">{t("models.answer.label")} </span>
        <span className="min-w-0 truncate">{formatList(names)}</span>
      </span>
      {reason !== "first" ? (
        <>
          <span aria-hidden="true">·</span>
          <span className="inline-flex items-center gap-1">
            <ReasonIcon aria-hidden="true" className="size-3 shrink-0" strokeWidth={1.75} />
            {t(`models.answer.reasons.${reason}`)}
          </span>
        </>
      ) : null}
    </span>
  );
}
